import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import {
  ACTION_CLOSED_STATUSES,
  actionLate,
  ACTION_STATUS_LABELS,
  formatDate,
  planActionSchema,
  projectActionListSchema,
  type ActionStatusName,
  type ProjectAction,
  type ProjectActionList,
  type ProjectActionsQuery,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { useMediaQuery } from '@/lib/use-media-query';

const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const WEEKDAY_NAMES = ['segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo'];

/** Cor do status (as mesmas do selo), e o atraso em vermelho por cima. */
const STATUS_LIGHT: Record<ActionStatusName, TrafficLight> = {
  PENDING: 'gray',
  IN_PROGRESS: 'blue',
  IN_VALIDATION: 'yellow',
  DONE: 'green',
  CANCELLED: 'gray',
};
const CHIP: Record<TrafficLight, string> = {
  green: 'bg-status-green-bg text-status-green-fg',
  yellow: 'bg-status-yellow-bg text-status-yellow-fg',
  red: 'bg-status-red-bg text-status-red-fg',
  gray: 'bg-status-gray-bg text-status-gray-fg',
  blue: 'bg-status-blue-bg text-status-blue-fg',
};

// Datas como dias ISO (AAAA-MM-DD), em UTC — sem fuso no meio.
const toDay = (d: Date) => d.toISOString().slice(0, 10);
const fromDay = (iso: string) => new Date(`${iso}T00:00:00Z`);
const addDays = (iso: string, n: number) => toDay(new Date(fromDay(iso).getTime() + n * 86_400_000));
/** Segunda-feira da semana do dia. */
const mondayOf = (iso: string) => addDays(iso, -((fromDay(iso).getUTCDay() + 6) % 7));
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export type CalendarMode = 'month' | 'week';

/** Dias exibidos: o mês inteiro em semanas completas (segunda a domingo) ou a semana do dia. */
export function calendarDays(mode: CalendarMode, anchor: string): string[] {
  if (mode === 'week') return Array.from({ length: 7 }, (_, i) => addDays(mondayOf(anchor), i));
  const first = `${anchor.slice(0, 7)}-01`;
  const d = fromDay(first);
  const last = toDay(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
  const start = mondayOf(first);
  const end = addDays(mondayOf(last), 6);
  const days: string[] = [];
  for (let day = start; day <= end; day = addDays(day, 1)) days.push(day);
  return days;
}

/**
 * Passo do teclado pelo dia: seta para os lados = um dia; para cima e para baixo = uma semana. O destino é a
 * célula do dia (as semanas do mês têm alturas diferentes), a partir do dia em que a ação está agora; fora do
 * período à vista, a ação não sai do lugar.
 */
const STEP_DAYS: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
const dayStep: KeyboardCoordinateGetter = (event, { active, context }) => {
  const delta = STEP_DAYS[event.code];
  if (delta === undefined) return undefined;
  event.preventDefault();
  const from =
    (context.over?.id as string | undefined) ??
    document
      .querySelector(`[data-action-id="${String(active)}"]`)
      ?.closest('[data-calendar-day]')
      ?.getAttribute('data-calendar-day') ??
    undefined;
  if (!from) return undefined;
  const target = document.querySelector(`[data-calendar-day="${addDays(from, delta)}"]`);
  if (!target) return undefined;
  const rect = target.getBoundingClientRect();
  // O canto do cartão dentro da célula de destino, abaixo do número do dia.
  return { x: rect.left + 6, y: rect.top + 28 };
};

/**
 * Calendário das ações pelo prazo (T-093, RF-33; DEC-043): mês e semana, cores por status e atraso; arrastar a
 * ação para outro dia muda o prazo (mouse, toque e teclado); clicar (ou Enter) abre o campo de data. No celular,
 * lista por dia.
 */
export function ActionCalendar({
  base,
  projectId,
  filters,
  canEdit,
  readOnly,
  onChanged,
}: {
  base: string;
  projectId: string;
  /** Filtros da tela (sem o intervalo de prazo, que é o período visível). */
  filters: Omit<ProjectActionsQuery, 'dueFrom' | 'dueTo'>;
  canEdit: boolean;
  /** Projeto arquivado: só consulta (a mensagem diz o motivo certo). */
  readOnly: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const wide = useMediaQuery('(min-width: 640px)');
  const today = localToday();
  const [mode, setMode] = useState<CalendarMode>('month');
  const [anchor, setAnchor] = useState(today);
  const [editing, setEditing] = useState<ProjectAction | null>(null);
  const days = calendarDays(mode, anchor);
  const range = { dueFrom: days[0]!, dueTo: days.at(-1)! };
  const key = ['action-plans', projectId, 'calendar', filters, range];
  const calendar = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`${base}/actions`, {
        query: Object.fromEntries(Object.entries({ ...filters, ...range }).filter(([, v]) => v)),
        schema: projectActionListSchema,
        signal,
      }),
    // Trocar filtro ou período não pisca o carregando: os dados anteriores ficam até chegarem os novos.
    placeholderData: keepPreviousData,
  });

  const move = useMutation({
    meta: { handlesError: true },
    mutationFn: ({ action, dueDate }: { action: ProjectAction; dueDate: string }) =>
      client.request(`${base}/${action.plan.id}/actions/${action.id}/due`, {
        method: 'PUT',
        body: { dueDate },
        schema: planActionSchema,
      }),
    // A ação muda de dia na hora; se a API recusar, volta (e o motivo aparece).
    onMutate: async ({ action, dueDate }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<ProjectActionList>(key);
      queryClient.setQueryData<ProjectActionList>(key, (data) =>
        data
          ? {
              ...data,
              items: data.items.map((a) =>
                a.id === action.id
                  ? { ...a, dueDate, late: actionLate({ dueDate, status: a.status }, today) }
                  : a,
              ),
            }
          : data,
      );
      return { before };
    },
    onError: (err, { action }, context) => {
      if (context?.before) queryClient.setQueryData(key, context.before);
      toast.error(`Não foi possível mudar o prazo de "${action.title}"`, { description: errorMessage(err) });
    },
    onSuccess: (a) => {
      setEditing(null);
      toast.success('Prazo alterado', {
        description: `${a.title}: ${a.dueDate ? formatDate(a.dueDate) : ''}`,
      });
    },
    onSettled: () => onChanged(),
  });

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    // Espaço pega e solta; Enter fica para abrir o campo de data.
    useSensor(KeyboardSensor, {
      coordinateGetter: dayStep,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );
  const items = calendar.data?.items ?? [];
  const titleOf = (id: unknown) => items.find((a) => a.id === id)?.title ?? 'a ação';
  const dayLabel = (id: unknown) => (typeof id === 'string' ? formatDate(id) : 'fora do calendário');
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Movendo ${titleOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${titleOf(active.id)} sobre ${dayLabel(over.id)}.`
        : `${titleOf(active.id)} fora do calendário.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${titleOf(active.id)} com prazo em ${dayLabel(over.id)}.`
        : `${titleOf(active.id)} voltou ao lugar.`,
    onDragCancel: ({ active }) => `Movimento de ${titleOf(active.id)} cancelado.`,
  };
  function onDragEnd({ active, over }: DragEndEvent) {
    const action = items.find((a) => a.id === active.id);
    const dueDate = over?.id as string | undefined;
    if (!action || !dueDate || dueDate === action.dueDate) return;
    move.mutate({ action, dueDate });
  }

  const step = (n: number) =>
    setAnchor((a) => {
      if (mode === 'week') return addDays(a, 7 * n);
      const d = fromDay(`${a.slice(0, 7)}-01`);
      return toDay(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)));
    });
  const title =
    mode === 'month'
      ? `${MONTH_NAMES[Number(anchor.slice(5, 7)) - 1]} de ${anchor.slice(0, 4)}`
      : `Semana de ${formatDate(days[0]!)} a ${formatDate(days[6]!)}`;
  const byDay = new Map<string, ProjectAction[]>();
  for (const a of items) if (a.dueDate) byDay.set(a.dueDate, [...(byDay.get(a.dueDate) ?? []), a]);
  // Com os dados do período anterior ainda na tela (a nova busca não chegou), nada se arrasta.
  const movable = (a: ProjectAction) =>
    canEdit && !ACTION_CLOSED_STATUSES.includes(a.status) && !move.isPending && !calendar.isPlaceholderData;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={mode === 'month' ? 'Mês anterior' : 'Semana anterior'}
            onClick={() => step(-1)}
          >
            <ChevronLeft aria-hidden />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(today)}>
            Hoje
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={mode === 'month' ? 'Próximo mês' : 'Próxima semana'}
            onClick={() => step(1)}
          >
            <ChevronRight aria-hidden />
          </Button>
          <h2 className="ml-2 text-base font-bold first-letter:uppercase" aria-live="polite">
            {title}
          </h2>
        </div>
        <div className="flex gap-1" role="group" aria-label="Período do calendário">
          {(
            [
              ['month', 'Mês'],
              ['week', 'Semana'],
            ] as const
          ).map(([m, label]) => (
            <Button
              key={m}
              size="sm"
              variant={mode === m ? 'primary' : 'outline'}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {calendar.isPending ? (
        <LoadingState rows={5} label="Carregando o calendário de ações" />
      ) : calendar.isError ? (
        <ErrorState
          error={calendar.error}
          onRetry={() => void calendar.refetch()}
          retrying={calendar.isFetching}
        />
      ) : (
        <>
          {calendar.data.total > items.length ? (
            <p className="rounded-md bg-muted px-3 py-2 text-sm" role="status">
              Mostrando {items.length} de {calendar.data.total} ações do período: use os filtros para ver as
              demais.
            </p>
          ) : null}
          {items.length === 0 ? (
            <EmptyState
              className="rounded-lg border border-dashed border-border py-10"
              icon={CalendarDays}
              title="Nenhuma ação com prazo neste período"
              description="As ações dos planos ativos que passam nos filtros aparecem no dia do prazo."
            />
          ) : null}
          {wide ? (
            <DndContext sensors={sensors} onDragEnd={onDragEnd} accessibility={{ announcements }}>
              <div
                role="grid"
                aria-label={`Calendário de ações: ${title}`}
                className="overflow-hidden rounded-lg border border-border"
              >
                <div
                  role="row"
                  className="grid grid-cols-7 border-b border-border bg-muted/50 text-xs font-semibold"
                >
                  {WEEKDAYS.map((w, i) => (
                    <div key={w} role="columnheader" aria-label={WEEKDAY_NAMES[i]} className="px-2 py-1.5">
                      {w}
                    </div>
                  ))}
                </div>
                {Array.from({ length: days.length / 7 }, (_, week) => (
                  <div key={week} role="row" className="grid grid-cols-7">
                    {days.slice(week * 7, week * 7 + 7).map((day) => (
                      <DayCell
                        key={day}
                        day={day}
                        muted={mode === 'month' && day.slice(0, 7) !== anchor.slice(0, 7)}
                        today={day === today}
                        tall={mode === 'week'}
                        actions={byDay.get(day) ?? []}
                        movable={movable}
                        onOpen={setEditing}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </DndContext>
          ) : (
            <ol aria-label={`Ações por dia: ${title}`} className="flex flex-col gap-3">
              {days
                .filter((d) => byDay.has(d))
                .map((day) => (
                  <li key={day}>
                    <h3 className={cn('text-sm font-semibold', day === today && 'text-brand')}>
                      {WEEKDAY_NAMES[(fromDay(day).getUTCDay() + 6) % 7]}, {formatDate(day)}
                      {day === today ? ' (hoje)' : ''}
                    </h3>
                    <ul className="mt-1.5 flex flex-col gap-1.5" aria-label={`Ações de ${formatDate(day)}`}>
                      {byDay.get(day)!.map((a) => (
                        <li key={a.id}>
                          <button
                            type="button"
                            onClick={() => setEditing(a)}
                            disabled={!canEdit}
                            className={cn(
                              'w-full rounded-md px-3 py-2 text-left text-sm disabled:cursor-default',
                              CHIP[STATUS_LIGHT[a.status]],
                              a.late && 'ring-2 ring-status-red-solid',
                            )}
                          >
                            <span className="font-medium">{a.title}</span>
                            <span className="block text-xs">
                              {ACTION_STATUS_LABELS[a.status]}
                              {a.late ? ' · Atrasada' : ''} · {a.plan.name}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
            </ol>
          )}
        </>
      )}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={move.isPending}
        title={editing ? `Prazo de ${editing.title}` : 'Prazo'}
        description={editing ? `${editing.plan.name} · ${ACTION_STATUS_LABELS[editing.status]}` : undefined}
      >
        {editing ? (
          <DueForm
            action={editing}
            canChange={movable(editing) || move.isPending}
            readOnly={readOnly}
            saving={move.isPending}
            onSubmit={(dueDate) => move.mutate({ action: editing, dueDate })}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function DayCell({
  day,
  muted,
  today,
  tall,
  actions,
  movable,
  onOpen,
}: {
  day: string;
  muted: boolean;
  today: boolean;
  tall: boolean;
  actions: ProjectAction[];
  movable: (a: ProjectAction) => boolean;
  onOpen: (a: ProjectAction) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: day });
  return (
    <div
      ref={setNodeRef}
      role="gridcell"
      data-calendar-day={day}
      aria-label={`${formatDate(day)}${today ? ' (hoje)' : ''}: ${actions.length === 1 ? '1 ação' : `${actions.length} ações`}`}
      className={cn(
        'min-w-0 border-r border-b border-border p-1.5 last:border-r-0',
        tall ? 'min-h-56' : 'min-h-28',
        muted && 'bg-muted/40 text-muted-foreground',
        isOver && 'bg-brand/10',
      )}
    >
      <p
        className={cn(
          'mb-1 text-xs font-semibold',
          today &&
            'inline-flex size-6 items-center justify-center rounded-full bg-brand text-brand-foreground',
        )}
      >
        {Number(day.slice(8, 10))}
      </p>
      <ul className="flex flex-col gap-1">
        {actions.map((a) => (
          <Chip key={a.id} action={a} movable={movable(a)} onOpen={onOpen} />
        ))}
      </ul>
    </div>
  );
}

function Chip({
  action: a,
  movable,
  onOpen,
}: {
  action: ProjectAction;
  movable: boolean;
  onOpen: (a: ProjectAction) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: a.id,
    disabled: !movable,
  });
  return (
    <li
      ref={setNodeRef}
      data-action-id={a.id}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn(isDragging && 'relative z-(--z-index-dropdown)')}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`${a.title} — ${ACTION_STATUS_LABELS[a.status]}${a.late ? ', atrasada' : ''}${movable ? '. Arraste para outro dia ou tecle Enter para mudar o prazo' : ''}`}
        onClick={() => onOpen(a)}
        className={cn(
          'w-full touch-none truncate rounded px-1.5 py-0.5 text-left text-xs font-medium',
          CHIP[STATUS_LIGHT[a.status]],
          a.late && 'ring-2 ring-status-red-solid',
          movable ? 'cursor-grab' : 'cursor-pointer',
          isDragging && 'shadow-md',
        )}
      >
        {a.title}
      </button>
    </li>
  );
}

/** Campo de data do prazo (alternativa ao arrastar; também é como se muda o prazo no celular). */
function DueForm({
  action,
  canChange,
  readOnly,
  saving,
  onSubmit,
  onCancel,
}: {
  action: ProjectAction;
  canChange: boolean;
  readOnly: boolean;
  saving: boolean;
  onSubmit: (dueDate: string) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [value, setValue] = useState(action.dueDate ?? '');
  const [error, setError] = useState<string | null>(null);
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!value) return setError('Informe o novo prazo.');
    if (action.startDate && value < action.startDate) {
      return setError(`O prazo precisa ser igual ou posterior ao início (${formatDate(action.startDate)}).`);
    }
    setError(null);
    onSubmit(value);
  }
  if (!canChange) {
    return (
      <div className="flex flex-col gap-3 text-sm">
        <p>
          Prazo: {action.dueDate ? formatDate(action.dueDate) : 'sem prazo'}.{' '}
          {ACTION_CLOSED_STATUSES.includes(action.status)
            ? 'Ação concluída ou cancelada não muda de prazo: reabra a ação para replanejar.'
            : readOnly
              ? 'O projeto está arquivado: os planos de ação ficam só para consulta.'
              : 'Seu perfil só consulta os planos de ação.'}
        </p>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onCancel}>
            Voltar ao calendário
          </Button>
        </div>
      </div>
    );
  }
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <FormField id={id} label="Novo prazo" error={error ?? undefined}>
        <Input
          {...fieldA11y(id, error ?? undefined)}
          type="date"
          value={value}
          min={action.startDate ?? undefined}
          onChange={(e) => setValue(e.target.value)}
          className="h-11"
        />
      </FormField>
      {action.late ? <FormAlert tone="error">Esta ação está atrasada.</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          Salvar prazo
        </Button>
      </div>
    </form>
  );
}
